import Modal from "components/DesignSystem/Modal/Modal";
import React from "react";
import { ModalProps } from "types/utils/modal";
import { Button } from "components/DesignSystem/Button/Button";
import Sunset from "static/images/Sunset.svg";
import { ThinArrowRight } from "components/icons/ThinArrowRight";
import classNames from "classnames";
import { CheckOutline } from "components/icons/CheckOutline";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { ConditionalLink } from "components/conditionalLink";

export const TaxYearlyPlanContent = ({
  isFromCart,
}: {
  isFromCart?: boolean;
}) => (
  <>
    <div className="t-text-purple t-text-subtext-sm">Billed yearly</div>
    <div className="t-text-text-100 t-mt-2 t-flex t-items-end">
      <span className="t-text-subtext">$300</span>
      <span className="t-text-body-sm">/year</span>
    </div>
    <div
      className={classNames("t-mt-1 t-text-caption t-italic", {
        "t-text-text-30": isFromCart,
        "t-text-dark_green-70": !isFromCart,
      })}
    >
      Saves you $60 annually 🎉
    </div>
  </>
);

export const TaxMonthlyContent = () => (
  <>
    <div className="t-line-through t-text-subtext-sm t-text-text-30">
      Billed monthly
    </div>
    <div className="t-text-text-30 t-mt-1.5 t-flex t-items-end">
      <span className="t-text-subtitle-lg t-line-through">$30</span>
      <span className="t-text-subtext t-line-through">/month</span>
    </div>
    <div className="t-mt-1 t-flex t-gap-0.5 t-items-end">
      <img src={Sunset} alt="Sunset" />
      <span className="t-text-caption t-italic t-text-red-50">
        Sunsetting plan
      </span>
    </div>
  </>
);

const PlanConvertUI = () => (
  <div className="t-flex t-gap-3 t-items-center t-mt-2.5">
    <div className="t-bg-surface-lighter-grey t-flex-1 t-basis-0 t-min-w-0 t-py-4 t-px-5 t-rounded-lg">
      <TaxMonthlyContent />
    </div>
    <ThinArrowRight className="t-text-purple-30 t-shrink-0" />
    <div className="t-flex-1 t-basis-0 t-py-[18px] t-px-5 t-bg-surface-purple t-rounded-lg">
      <TaxYearlyPlanContent />
    </div>
  </div>
);

export const TaxSubscriptionUpdate = ({ isOpen, close }: ModalProps) => {
  const group = useCurrentGroup();
  const generalHelpChannel = group?.secondary_channel_url;

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Subscription Billing Update</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-space-y-6">
          <div className="t-space-y-4">
            <div className="t-text-subtitle-lg t-text-text-60">
              Here’s an overview of how the subscription will look now:
            </div>
            <PlanConvertUI />
          </div>
          <div className="t-space-y-[14px]">
            <div className="t-text-subtitle-lg t-text-text-60">
              What happens next if you are on a monthly subscription?
            </div>
            <div className="t-flex t-flex-col t-gap-3">
              <div className="t-flex t-gap-3 t-items-start">
                <CheckOutline className="t-text-dark_green t-shrink-0" />
                <div className="t-text-body-sm t-text-text-30">
                  Your subscription plan will automatically be upgraded to the
                  annual subscription.
                </div>
              </div>
              <div className="t-flex t-gap-3 t-items-start">
                <CheckOutline className="t-text-dark_green t-shrink-0" />
                <div className="t-text-body-sm t-text-text-30">
                  If you’ve already paid for this month, we’ll adjust that
                  amount in your current billing. You’ll only pay the remaining
                  balance now, and regular annual pricing will continue from the
                  next cycle.
                </div>
              </div>
              <div className="t-flex t-gap-3 t-items-start">
                <CheckOutline className="t-text-dark_green t-shrink-0" />
                <div className="t-text-body-sm t-text-text-30">
                  Nothing changes in your service experience. You simply pay
                  once a year instead of every month.
                </div>
              </div>
            </div>
          </div>
          <div className="t-text-text-30 t-text-body-sm">
            If you have any questions or want to discuss this further, feel free
            to contact us .
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <ConditionalLink to={`/chat/${generalHelpChannel}`}>
            <Button>Talk to support</Button>
          </ConditionalLink>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
